import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {format} from 'date-fns';
import {Check, Loader2, Search} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {toast} from '@common-web/hooks/use-toast';
import {trpc, type RouterOutput} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {TenantTag} from '@internal/pages/group-pipeline/components/TenantTag';
import {VBA_STATUS_DISPLAY} from '@internal/pages/group-pipeline/components/VbaStatusIndicator';

type Admin123Result = RouterOutput['internal']['groupPipeline']['admin123']['search'][number];

// Why a result can't be added, if it can't.
const blockedReason = (row: Admin123Result) =>
  row.hasPipeline
    ? 'Already in the pipeline'
    : row.onSeveralVbaGroups
      ? 'On several VBA groups. Add the right one from VBA.'
      : null;

// Starts a pipeline for an Admin123 group. VBA may not have it yet: the pipeline then uses the ID
// the sync will build it under.
export function StartFromAdmin123Dialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const utils = trpc.useUtils();
  const [picked, setPicked] = useState<Admin123Result | null>(null);
  const [receivedOn, setReceivedOn] = useState(format(new Date(), 'yyyy-MM-dd'));

  const start = trpc.internal.groupPipeline.admin123.start.useMutation({
    onSuccess: async ({vbaGroupId}) => {
      await Promise.all([
        utils.internal.groupPipeline.list.invalidate(),
        utils.internal.groupPipeline.board.invalidate(),
        utils.internal.groupPipeline.boardColumn.invalidate(),
        utils.internal.groupPipeline.admin123.search.invalidate(),
        utils.internal.groupPipeline.detail.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.activity.invalidate({vbaGroupId}),
      ]);
      toast({title: 'Added to the pipeline'});
      onOpenChange(false);
      // Keep wherever Back already pointed (the list or board), or come back here.
      const from =
        (location.state as {from?: string} | null)?.from ?? location.pathname + location.search;
      navigate(`/group-pipeline/${vbaGroupId}?tab=tasks`, {state: {from}});
    },
  });

  useEffect(() => {
    if (open) {
      setPicked(null);
      setReceivedOn(format(new Date(), 'yyyy-MM-dd'));
      start.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = () => {
    if (!picked || !receivedOn) return;
    start.mutate({
      admin123AgentId: picked.agentId,
      // Noon local time keeps the picked day from shifting across time zones.
      receivedAt: new Date(`${receivedOn}T12:00:00`),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='grid-cols-[minmax(0,1fr)] sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Add from Admin123</DialogTitle>
          <DialogDescription>
            For groups CBS is working on that VBA may not have yet. Their details come from Admin123
            until the sync builds them in VBA.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label>Group</Label>
            <Admin123Picker picked={picked} onPick={setPicked} />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='start-admin123-received'>RFC received on</Label>
            <Input
              id='start-admin123-received'
              type='date'
              value={receivedOn}
              max={format(new Date(), 'yyyy-MM-dd')}
              onChange={(e) => setReceivedOn(e.target.value)}
              className='w-48'
            />
            <p className='text-xs text-gray-500'>
              Due dates count from this day. Use the real date for groups already in progress.
            </p>
          </div>

          {start.error && (
            <p role='alert' className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
              {start.error.message}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!picked || !receivedOn || start.isPending} onClick={submit}>
            {start.isPending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
            Add to pipeline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Admin123Picker({
  picked,
  onPick,
}: {
  picked: Admin123Result | null;
  onPick: (group: Admin123Result) => void;
}) {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const results = trpc.internal.groupPipeline.admin123.search.useQuery(
    {search: search || undefined},
    // Keep the last results up while the next search loads, so the list doesn't flash.
    {placeholderData: (previous) => previous},
  );
  const searching = input.trim() !== search || results.isFetching;

  return (
    <div className='space-y-2'>
      <div className='relative'>
        <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500' />
        <Input
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder='Search name or agent ID'
          className='pl-9 pr-9'
        />
        {searching && (
          <Loader2 className='absolute inset-y-0 right-3 my-auto h-4 w-4 animate-spin text-gray-400' />
        )}
      </div>
      {/* Fixed height so the dialog doesn't resize as results change. */}
      <div className='h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 [scrollbar-gutter:stable]'>
        {!results.data ? (
          <PickerMessage>
            {results.isError ? 'Could not load groups.' : 'Loading groups…'}
          </PickerMessage>
        ) : results.data.length === 0 ? (
          <PickerMessage>
            {search ? 'No Admin123 groups match that search.' : 'No Admin123 groups found.'}
          </PickerMessage>
        ) : (
          results.data.map((row) => {
            const selected = picked?.agentId === row.agentId;
            const blocked = blockedReason(row);
            return (
              <button
                key={row.agentId}
                type='button'
                disabled={Boolean(blocked)}
                onClick={() => onPick(row)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left enabled:hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50',
                  selected && 'bg-gray-100',
                )}
              >
                <div className='min-w-0 flex-1'>
                  {/* Two lines fit nearly every name, so no hover is needed to read it. */}
                  <p className='line-clamp-2 text-sm font-medium'>{row.name}</p>
                  <p className='flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500'>
                    {[
                      `Agent ID ${row.agentId}`,
                      row.inVba ? `VBA ID ${row.vbaGroupId}` : <NotInVba />,
                      !row.isActive && 'Inactive in Admin123',
                      blocked,
                    ]
                      .filter(Boolean)
                      .map((part, i) => (
                        <span key={i} className='inline-flex items-center gap-1.5'>
                          {i > 0 && <span aria-hidden>·</span>}
                          {part}
                        </span>
                      ))}
                  </p>
                </div>
                <TenantTag shortName={row.tenantName} />
                <Check className={cn('h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function NotInVba() {
  const {icon: Icon, label, color} = VBA_STATUS_DISPLAY.NOT_IN_VBA;
  return (
    <span className={cn('inline-flex items-center gap-1', color)}>
      <Icon className='h-3.5 w-3.5 shrink-0 -translate-y-px' />
      {label}
    </span>
  );
}

function PickerMessage({children}: {children: string}) {
  return (
    <p className='flex h-full items-center justify-center px-3 text-center text-sm text-gray-500'>
      {children}
    </p>
  );
}
